import {
  Box,
  Card,
  Typography,
  Chip,
  Grid,
  Divider,
} from "@mui/material";

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import GradientButton from "../Button";

export default function QuizResult() {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state;

  const stored = JSON.parse(
    localStorage.getItem("quizResult") || "{}"
  );

  const quiz = state?.quiz || stored.quiz;
  const result = state?.result || stored.result;

  /*
   * Exit fullscreen + prevent back navigation
   */
  useEffect(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }

    window.history.pushState(
      null,
      "",
      window.location.href
    );

    window.onpopstate = () => {
      window.history.pushState(
        null,
        "",
        window.location.href
      );
    };

    return () => {
      window.onpopstate = null;
    };
  }, []);

  /*
   * Save result to localStorage
   */
  useEffect(() => {
    if (quiz && result) {
      localStorage.setItem(
        "quizResult",
        JSON.stringify({
          quiz,
          result,
        })
      );
    }
  }, [quiz, result]);

  /*
   * Validate
   */
  if (!quiz || !result) {
    return (
      <Typography
        align="center"
        mt={5}
      >
        Invalid quiz result
      </Typography>
    );
  }

  const {
    score = 0,
    totalQuestions = 0,
    correctAnswers = 0,
    wrongAnswers = 0,
    unanswered = 0,
    percentage = 0,
    attemptNumber = 1,
  } = result;

  const passed = percentage >= 60;

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 720,
          p: {
            xs: 3,
            md: 5,
          },
          textAlign: "center",
          borderRadius: 3,
          boxShadow:
            "0px 15px 40px rgba(0,0,0,0.15)",
          backgroundColor: (theme) =>
            theme.palette.background.paper,
        }}
      >
        {/* TITLE */}

        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
        >
          Quiz Result
        </Typography>

        <Typography
          variant="subtitle1"
          mt={1}
          mb={3}
          color="text.secondary"
        >
          {quiz.title}
        </Typography>

        {/* ATTEMPT NUMBER */}

        <Chip
          label={`Attempt ${attemptNumber}`}
          variant="outlined"
          sx={{
            mb: 3,
          }}
        />

        {/* SCORE CIRCLE */}

        <Box
          sx={{
            width: 160,
            height: 160,
            borderRadius: "50%",
            mx: "auto",
            mb: 3,

            border: "2px solid #6366F1",

            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",

            backgroundColor: "#EEF2FF",
          }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            color="primary"
          >
            {score}/{totalQuestions}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            SCORE
          </Typography>
        </Box>

        {/* PASS / FAIL */}

        <Chip
          label={passed ? "Passed" : "Failed"}
          color={passed ? "success" : "error"}
          sx={{
            px: 2,
            py: 1,
            fontSize: "0.9rem",
            mb: 3,
          }}
        />

        {/* PERCENTAGE */}

        <Typography
          variant="h6"
          fontWeight={600}
          mb={3}
        >
          Percentage: {percentage}%
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* STATISTICS */}

        <Grid
          container
          spacing={2}
          sx={{
            mb: 4,
          }}
        >
          {/* CORRECT */}

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#ECFDF5",
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                color="success.main"
              >
                {correctAnswers}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Correct
              </Typography>
            </Box>
          </Grid>

          {/* WRONG */}

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#FEF2F2",
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                color="error.main"
              >
                {wrongAnswers}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Wrong
              </Typography>
            </Box>
          </Grid>

          {/* UNANSWERED */}

          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#F3F4F6",
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                color="text.secondary"
              >
                {unanswered}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Unanswered
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* TOTAL QUESTIONS */}

        <Typography
          variant="body2"
          color="text.secondary"
          mb={3}
        >
          Total Questions: {totalQuestions}
        </Typography>

        {/* BACK */}

        <GradientButton
          variant="contained"
          size="large"
          onClick={() => {
            localStorage.removeItem("quizResult");

            navigate("/quizzer", {
              state: {
                refresh: Date.now(),
              },
            });
          }}
        >
          ← Back Quiz
        </GradientButton>
      </Card>
    </Box>
  );
}